import type { FC, SyntheticEvent } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TabContext, TabList, TabPanel } from '@mui/lab';
import {
  Box,
  CircularProgress,
  Grid,
  Stack,
  Tab,
  Typography,
} from '@mui/material';

import type { Filter } from 'src/types/followUpInspirations';
import type { Survey } from 'src/types/survey';
import Modal, { MODAL_TEST_ID } from 'src/components/common/Modal';
import ButtonAdditionalInfoModal from 'src/components/followUpInspirations/components/InspirationsPage/components/ButtonAdditionalInfoModal';
import PanelContent from 'src/components/followUpInspirations/components/InspirationsPage/components/PanelContent';
import useInspirationsQuery from 'src/components/followUpInspirations/hooks/useInspirationsQuery';

import { styles } from './index.styles';

export const INSPIRATION_PANEL_TEST_ID = 'inspiration-panel';
export const INSPIRATION_PANEL_LOADING_TEST_ID = 'inspiration-panel-loading';

interface InspirationPageProps {
  survey: Survey;
  filterResult: { [key: string]: Filter };
}

const InspirationsPage: FC<InspirationPageProps> = ({
  survey,
  filterResult,
}) => {
  const { t } = useTranslation('common');
  const initialTabValue = '0';
  const [tabValue, setTabValue] = useState<string>(initialTabValue);
  const [showModal, setShowModal] = useState<boolean>(false);
  const handleChange = (_event: SyntheticEvent, newValue: string) => {
    setTabValue(newValue);
  };

  const canFetch = useMemo(() => {
    const filterValues = filterResult ? Object.values(filterResult) : [];
    return (
      !!survey &&
      filterValues.length > 0 &&
      filterValues.every((value) => value !== null)
    );
  }, [filterResult, survey]);

  const {
    data: inspirations,
    isLoading,
    error,
  } = useInspirationsQuery(survey, filterResult, canFetch);
  const inspirationRef = useRef<HTMLElement>();

  useEffect(() => {
    if (inspirationRef.current && canFetch && isLoading) {
      const element = inspirationRef.current;
      element.scrollIntoView && element.scrollIntoView({ behavior: 'smooth' });
    }
  }, [isLoading, canFetch]);

  useEffect(() => {
    setShowModal(!isLoading && !!error);
  }, [isLoading, error]);

  const closeModal = () => {
    setShowModal(false);
  };

  return (
    <>
      <Modal
        isOpen={showModal}
        onClose={closeModal}
        data-test-id={MODAL_TEST_ID}
      >
        <p>{t(error as string)}</p>
      </Modal>
      <Box
        sx={styles.inspirationDataWrapper}
        data-test-id={INSPIRATION_PANEL_TEST_ID}
        ref={inspirationRef}
      >
        {canFetch && isLoading && (
          <Stack
            direction="row"
            data-test-id={INSPIRATION_PANEL_LOADING_TEST_ID}
            sx={{
              justifyContent: 'center',
              py: 45,
            }}
          >
            <CircularProgress />
          </Stack>
        )}
        {canFetch && !isLoading && !!inspirations && inspirations.length > 0 ? (
          <TabContext value={tabValue}>
            <Stack
              direction="row"
              sx={{
                justifyContent: 'space-between',
                flexWrap: 'wrap-reverse',
              }}
            >
              <TabList onChange={handleChange} sx={styles.tabList}>
                {inspirations.map((_data, i) => (
                  <Tab
                    label={`${t('followUpInspirations:inspiration')} ${i + 1}`}
                    value={String(i)}
                    key={i}
                  />
                ))}
              </TabList>
              <Stack
                sx={{
                  pb: 1,
                  ml: 'auto',
                  justifyContent: 'center',
                }}
              >
                <ButtonAdditionalInfoModal />
              </Stack>
            </Stack>
            {inspirations.map((inspiration, i) => (
              <TabPanel value={String(i)} sx={styles.tabPanel} key={i}>
                <PanelContent
                  inspiration={inspiration}
                  filterResult={filterResult}
                  t={t}
                />
              </TabPanel>
            ))}
          </TabContext>
        ) : (
          <Grid container rowSpacing={1.25} columnSpacing={1.25}>
            <Grid item xs={12}>
              <Typography variant="h6" sx={styles.noDataAvailable}>
                {t('common:noDataAvailable')}
              </Typography>
            </Grid>
          </Grid>
        )}
      </Box>
    </>
  );
};

export default InspirationsPage;
